import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchItem,
  fetchItems,
  fetchSuggestedSlots,
  putAwayItem,
  moveItem,
  placeItem,
  reserveItem,
  unreserveItem,
  scrapItem,
  patchSlot,
} from './warehouseApi.js';

// Stale windows:
//   - 30s for list/detail reads — mutations invalidate explicitly on success,
//     so correctness is unaffected; this just stops refetching on every
//     component remount (tab flip, drawer open) for data that almost never
//     changes without going through our own mutations first.
const READ_STALE_MS = 30_000;

export function useItems(filters = {}) {
  return useQuery({
    queryKey: ['warehouse', 'items', filters],
    queryFn: () => fetchItems(filters),
    staleTime: READ_STALE_MS,
  });
}

export function useItem(serial) {
  return useQuery({
    queryKey: ['warehouse', 'item', serial],
    queryFn: () => fetchItem(serial),
    enabled: !!serial,
    staleTime: READ_STALE_MS,
  });
}

export function useSuggestedSlots(serial, buildingId) {
  return useQuery({
    queryKey: ['warehouse', 'suggest', serial, buildingId],
    queryFn: () => fetchSuggestedSlots(serial, buildingId),
    enabled: !!serial,
  });
}

// Mutations invalidate occupancy (map), items (sidebars), and movements
// (dashboard feed). TanStack v5 matches hierarchically, so invalidating
// ['warehouse','occupancy'] also catches ['warehouse','occupancy', buildingId].
function invalidateWarehouseReads(queryClient) {
  queryClient.invalidateQueries({ queryKey: ['warehouse', 'occupancy'] });
  queryClient.invalidateQueries({ queryKey: ['warehouse', 'items'] });
  queryClient.invalidateQueries({ queryKey: ['warehouse', 'movements'] });
}

export function usePutAway() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serial, slotCode }) => putAwayItem(serial, slotCode),
    onSuccess: () => invalidateWarehouseReads(queryClient),
  });
}

export function useMove() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serial, toSlotCode }) => moveItem(serial, toSlotCode),
    onSuccess: () => invalidateWarehouseReads(queryClient),
  });
}

// Drag-and-drop placement (yard→slot or slot→slot), with cross-type `force`.
export function usePlaceItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serial, toSlotCode, force }) =>
      placeItem(serial, toSlotCode, force),
    onSuccess: () => invalidateWarehouseReads(queryClient),
  });
}

export function useReserve() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serial, orderId }) => reserveItem(serial, orderId),
    onSuccess: () => invalidateWarehouseReads(queryClient),
  });
}

export function useUnreserve() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serial }) => unreserveItem(serial),
    onSuccess: () => invalidateWarehouseReads(queryClient),
  });
}

export function useScrap() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serial, reason }) => scrapItem(serial, reason),
    onSuccess: () => invalidateWarehouseReads(queryClient),
  });
}

// Generic slot patch — used by the block / unblock buttons on the drawer.
export function usePatchSlot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, updates }) => patchSlot(code, updates),
    onSuccess: () => invalidateWarehouseReads(queryClient),
  });
}
